/**
 * Notes Page
 *
 * Features:
 * - Quick capture input for fast note creation
 * - Markdown editor with formatting toolbar
 * - Tag-based organization and filtering
 * - Searchable note list
 * - Supabase persistence
 */

import { useState, useCallback, useEffect } from 'react';
import { QuickCapture } from '@/components/Notes/QuickCapture';
import { NoteList } from '@/components/Notes/NoteList';
import { NoteEditor } from '@/components/Notes/NoteEditor';
import { TagFilter } from '@/components/Notes/TagFilter';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import './Notes.css';

export interface Note {
  id: string;
  title: string;
  content: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  pinned: boolean;
}

export function Notes(): JSX.Element {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch notes from Supabase
  useEffect(() => {
    async function fetchNotes(): Promise<void> {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('notes')
        .select(`
          id,
          title,
          content,
          pinned,
          created_at,
          updated_at,
          note_tags (tag)
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch notes:', error);
        setLoading(false);
        return;
      }

      const formattedNotes: Note[] = (data ?? []).map((note) => ({
        id: note.id,
        title: note.title,
        content: note.content,
        pinned: note.pinned,
        created_at: note.created_at,
        updated_at: note.updated_at,
        tags: (note.note_tags as { tag: string }[]).map((t) => t.tag),
      }));

      setNotes(formattedNotes);

      // Extract unique tags
      const uniqueTags = new Set<string>();
      formattedNotes.forEach((note) => note.tags.forEach((tag) => uniqueTags.add(tag)));
      setAllTags(Array.from(uniqueTags).sort());

      setLoading(false);
    }

    fetchNotes();
  }, [user]);

  const filteredNotes = notes.filter((note) => {
    const matchesSearch =
      searchQuery === '' ||
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 || selectedTags.some((tag) => note.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  const sortedNotes = [...filteredNotes].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  const handleQuickCapture = useCallback(async (content: string): Promise<void> => {
    if (!user) return;

    const title = content.split('\n')[0]?.slice(0, 50) ?? 'Untitled';

    const { data, error } = await supabase
      .from('notes')
      .insert({
        user_id: user.id,
        title,
        content,
        pinned: false,
      })
      .select('id, title, content, pinned, created_at, updated_at')
      .single();

    if (error) {
      console.error('Failed to create note:', error);
      return;
    }

    const newNote: Note = {
      id: data.id,
      title: data.title,
      content: data.content,
      pinned: data.pinned,
      created_at: data.created_at,
      updated_at: data.updated_at,
      tags: [],
    };

    setNotes((prev) => [newNote, ...prev]);
  }, [user]);

  const handleSaveNote = useCallback(async (note: Note): Promise<void> => {
    if (!user) return;

    const existing = notes.find((n) => n.id === note.id);

    if (existing) {
      // Update existing note
      const { error } = await supabase
        .from('notes')
        .update({
          title: note.title,
          content: note.content,
          pinned: note.pinned,
        })
        .eq('id', note.id);

      if (error) {
        console.error('Failed to update note:', error);
        return;
      }

      // Update tags - delete existing and insert new
      await supabase.from('note_tags').delete().eq('note_id', note.id);

      if (note.tags.length > 0) {
        await supabase.from('note_tags').insert(
          note.tags.map((tag) => ({ note_id: note.id, tag }))
        );
      }

      setNotes((prev) =>
        prev.map((n) =>
          n.id === note.id ? { ...note, updated_at: new Date().toISOString() } : n
        )
      );
    } else {
      // Create new note
      const { data, error } = await supabase
        .from('notes')
        .insert({
          user_id: user.id,
          title: note.title,
          content: note.content,
          pinned: note.pinned,
        })
        .select('id, title, content, pinned, created_at, updated_at')
        .single();

      if (error) {
        console.error('Failed to create note:', error);
        return;
      }

      // Insert tags if any
      if (note.tags.length > 0) {
        await supabase.from('note_tags').insert(
          note.tags.map((tag) => ({ note_id: data.id, tag }))
        );
      }

      const newNote: Note = {
        id: data.id,
        title: data.title,
        content: data.content,
        pinned: data.pinned,
        created_at: data.created_at,
        updated_at: data.updated_at,
        tags: note.tags,
      };

      setNotes((prev) => [newNote, ...prev]);
    }

    // Update tags list
    const uniqueTags = new Set<string>(allTags);
    note.tags.forEach((tag) => uniqueTags.add(tag));
    setAllTags(Array.from(uniqueTags).sort());

    setEditingNote(null);
    setIsCreating(false);
  }, [user, notes, allTags]);

  const handleDeleteNote = useCallback(async (noteId: string): Promise<void> => {
    const { error } = await supabase.from('notes').delete().eq('id', noteId);

    if (error) {
      console.error('Failed to delete note:', error);
      return;
    }

    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    setEditingNote(null);
  }, []);

  const handleTogglePin = useCallback(async (noteId: string): Promise<void> => {
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;

    const { error } = await supabase
      .from('notes')
      .update({ pinned: !note.pinned })
      .eq('id', noteId);

    if (error) {
      console.error('Failed to toggle pin:', error);
      return;
    }

    setNotes((prev) =>
      prev.map((n) =>
        n.id === noteId ? { ...n, pinned: !n.pinned, updated_at: new Date().toISOString() } : n
      )
    );
  }, [notes]);

  const handleCreateNew = useCallback(() => {
    const newNote: Note = {
      id: `temp-${Date.now()}`,
      title: '',
      content: '',
      tags: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      pinned: false,
    };
    setEditingNote(newNote);
    setIsCreating(true);
  }, []);

  if (editingNote) {
    return (
      <div className="notes">
        <NoteEditor
          note={editingNote}
          isNew={isCreating}
          onSave={handleSaveNote}
          onDelete={handleDeleteNote}
          onCancel={() => {
            setEditingNote(null);
            setIsCreating(false);
          }}
          availableTags={allTags}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="notes">
        <div className="notes__header">
          <h1 className="notes__title">Notes</h1>
        </div>
        <div className="notes__loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="notes">
      <div className="notes__header">
        <h1 className="notes__title">Notes</h1>
        <button type="button" className="notes__new-button" onClick={handleCreateNew}>
          + New Note
        </button>
      </div>

      <QuickCapture onCapture={handleQuickCapture} />

      <div className="notes__filters">
        <div className="notes__search">
          <input
            type="text"
            className="notes__search-input"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <TagFilter
          tags={allTags}
          selectedTags={selectedTags}
          onTagSelect={setSelectedTags}
          noteCounts={allTags.reduce(
            (acc, tag) => {
              acc[tag] = notes.filter((n) => n.tags.includes(tag)).length;
              return acc;
            },
            {} as Record<string, number>
          )}
        />
      </div>

      <NoteList
        notes={sortedNotes}
        onNoteClick={(note) => setEditingNote(note)}
        onTogglePin={handleTogglePin}
      />
    </div>
  );
}
